import React, { useState, useEffect, useRef } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import Filters from "components/Common/Filters"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import TableShimmer from "components/Shared/TableShimmer"
import DeleteModal from "components/Common/DeleteModal"
import {
  deleteBooking,
  getAllBookings,
  updateBookingStatus,
} from "store/actions/bookingsAction"
import formatDateTime from "components/Shared/formatDateTime"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import isPermissionEnabled from "common/isPermissionEnabled"
import { bookingStatus } from "components/Common/constants"
import DownloadCSV from "components/Shared/DownloadCSV"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import Select from "components/Shared/TranslatedSelect"
import { useTranslation } from "react-i18next"

const Bookings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Bookings List ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const typeParam = searchParams.get("type")
  const minDate = searchParams.get("minDate")
  const maxDate = searchParams.get("maxDate")

  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "0"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    type: typeParam
      ? typeParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
  })

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [remark, setRemark] = useState("")
  const [originalTicketStatus, setOriginalTicketStatus] = useState(null)
  const [selectedTicketStatus, setSelectedTicketStatus] = useState(null)
  const [isStatusUpdated, setIsStatusUpdated] = useState(false)

  const toggleModal = () => {
    if (isModalOpen) {
      setSelectedStatus(null)
      setRemark(null)
      setOriginalTicketStatus(null)
      setSelectedTicketStatus(null)
    }
    setIsModalOpen(!isModalOpen)
  }

  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    bookings,
  } = useSelector(state => state.Bookings)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      21,
      "booking_1",
      accountType
    )

    const isPermissionDelete = isPermissionEnabled(
      userRole,
      21,
      "booking_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    getBookings()
  }, [])

  const getBookings = () => {
    const { status, sortOrder, type } = selectedFilters
    const updatedType = type?.map(item => item.value) || []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status, // 0 all , 1 alive, 2 over
      orderBy: sortOrder,
      type: updatedType, // premium, truck
      userId: "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
      keyword: searchTerm,
    }
    dispatch(getAllBookings(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 0 },
        { label: t("Pending"), value: 1 },
        { label: t("Open"), value: 2 },
        { label: t("Confirmed"), value: 3 },
        { label: t("Completed"), value: 4 },
        { label: t("Cancelled"), value: 5 },
        { label: t("Rejected"), value: 6 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "type",
      type: "multiSelect",
      options: [
        { label: t("Premium"), value: "premium" },
        { label: t("Truck"), value: "truck" },
      ],
      placeholder: t("Select Type"),
    },
  ]

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleFetchBookings = (page = 1) => {
    const { status, sortOrder, type } = selectedFilters
    const updatedType = type?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    if (searchTerm) searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())
    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }
    if (type.length > 0) {
      const typeString = type
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("type", typeString)
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type: updatedType,
      userId: "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllBookings(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/bookings?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchBookings(1)
  }

  const onPageChange = page => {
    handleFetchBookings(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 0,
      orderBy: 2,
      type: [], // premium, truck
      userId: "",
      dateRange: {},
      keyword: "",
    }
    dispatch(getAllBookings(payload))
    navigate(`/bookings?page=${1}`)
    setSelectedFilters({
      status: 0,
      sortOrder: 2,
      type: [],
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteBooking(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const renderTicketStatus = actionType => {
    return bookingStatus[actionType] || ""
  }

  const handleStatusChange = () => {
    const payload = {
      id: selectedId,
      status: `${selectedTicketStatus}`,
      remark,
    }
    dispatch(updateBookingStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          toggleModal()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const exportToCSV = async () => {
    const { status, sortOrder, type } = selectedFilters
    const updatedType = type?.map(item => item.value) || []

    // DownloadCSV(formattedData, filename)
    const payload = {
      status: Number(status),
      type: "BOOKING",
      exportType: "csv",
      bookingType: updatedType,
      userId: "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Booking"))
    }
  }

  const handleExcelDownload = async () => {
    const { status, sortOrder, type } = selectedFilters
    const updatedType = type?.map(item => item.value) || []

    // DownloadExcel(formattedData, filename)
    const payload = {
      status: Number(status),
      type: "BOOKING",
      exportType: "xlsx",
      bookingType: updatedType,
      userId: "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Booking"))
    }
  }

  // Convert bookingStatus to options array for react-select
  // const statusOptions = Object.entries(bookingStatus).map(([value, label]) => ({
  //   value,
  //   label: t(label),
  // }))
  const statusOptions = [
    { value: 1, label: t("Pending") },
    { value: 2, label: t("Open") },
    { value: 3, label: t("Confirmed") },
    { value: 4, label: t("Completed") },
    { value: 5, label: t("Cancelled") },
    { value: 6, label: t("Rejected") },
  ]

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Bookings List")}
                isExportData={loading ? false : true}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
              />
              <CardBody>
                {" "}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name, Phone Number & Booking Id")}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  colSize={4}
                />
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : bookings.length === 0 ? (
                    <p className="text-center my-2">{t("No bookings found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Booking Id")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Type")}</th>
                          <th>{t("City Of Event")}</th>
                          {/* <th>Venue</th> */}
                          <th>{t("Date & Time Of Event")}</th>
                          <th>{t("Status")}</th>
                          {/* {usersPermissions.edit && */}
                          <th style={{ minWidth: 200 }}>
                            {t("Update Status")}
                          </th>
                          <th>{t("Action")}</th>
                          {/* } */}
                        </tr>
                      </thead>
                      <tbody>
                        {bookings.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              navigate(
                                `/bookings/booking-details?id=${item._id}`
                              )
                            }}
                            className="cursor-pointer capitalize_td"
                          >
                            <th scope="row">
                              {(currentPage - 1) * 10 + index + 1}
                            </th>
                            <td>{item.bookingId}</td>
                            <td>{item.fullName}</td>
                            <td>{t(item.type)}</td>
                            <td>{item.cityOfEvent}</td>
                            {/* <td>{item.venue}</td> */}
                            <td>{formatDateTime(item?.dateTimeOfEvent)}</td>
                            <td>{t(renderTicketStatus(item.status))}</td>
                            <td onClick={e => e.stopPropagation()}>
                              <Button
                                color="primary"
                                onClick={() => {
                                  setSelectedId(item._id)
                                  toggleModal()
                                  setSelectedTicketStatus(item.status)
                                  setOriginalTicketStatus(item.status)
                                  setIsStatusUpdated(false)
                                }}
                                disabled={
                                  item.status === 4 ||
                                  item.status === 5 ||
                                  item.status === 6
                                }
                              >
                                {t("Update Status")}
                              </Button>
                              {/* <Select
                                value={statusOptions.find(
                                  option =>
                                    option.value === item.status.toString()
                                )}
                                isDisabled={
                                  selectedId === item._id && isLoading
                                }
                                onChange={(selectedOption, event) => {
                                  if (event.nativeEvent) {
                                    event.nativeEvent.stopPropagation()
                                  }
                                  handleStatusChange(
                                    item._id,
                                    selectedOption.value
                                  )
                                }}
                                options={statusOptions
                                  .filter(option => {
                                    const optionValue = parseInt(option.value)
                                    const currentStatus = parseInt(item.status)

                                    // If current status is Completed (4), only show Completed
                                    if (currentStatus === 4) {
                                      return optionValue === 4
                                    }

                                    // For other statuses, show the current status and higher options
                                    return optionValue >= currentStatus
                                  })
                                  .map(option => ({
                                    ...option,
                                    isDisabled:
                                      parseInt(option.value) ===
                                      parseInt(item.status),
                                  }))}
                                placeholder={t("Update Status")}
                              /> */}
                            </td>

                            {/* {usersPermissions.edit && ( */}
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <>
                                  <i
                                    id={`edittooltip_${item._id}`}
                                    onClick={e => {
                                      e.stopPropagation()
                                      navigate(
                                        `/bookings/booking-details?id=${item._id}`
                                      )
                                    }}
                                    className="mdi mdi-eye font-size-18 text-primary mx-2 cursor-pointer"
                                  ></i>
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`edittooltip_${item._id}`}
                                  >
                                    {t("View")}
                                  </UncontrolledTooltip>
                                </>
                                {usersPermissions.delete && (
                                  <>
                                    <i
                                      id={`deletetooltip_${item._id}`}
                                      onClick={e => {
                                        e.stopPropagation()
                                        setItemIdToDelete(item._id)
                                        setShowDeleteModal(true)
                                      }}
                                      className="bx bxs-trash font-size-18 mx-2 cursor-pointer"
                                      style={{ color: "#FD5F62" }}
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`deletetooltip_${item._id}`}
                                    >
                                      {t("Delete")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && bookings.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={bookings}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Booking"),
        })}
      />
      <Modal
        isOpen={isModalOpen}
        toggle={toggleModal}
        className=""
        centered
        size="md"
      >
        <ModalHeader toggle={toggleModal}>{t("Update Status")}</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg={12}>
              <div className="">
                <label htmlFor="">{t("Select Status")}</label>
                <Select
                  value={statusOptions.find(
                    option => option.value === selectedTicketStatus
                  )}
                  isDisabled={isLoading}
                  onChange={(selectedOption, event) => {
                    const { value } = selectedOption
                    setSelectedTicketStatus(value)
                    // Mark as updated only if the new status is different
                    setIsStatusUpdated(value !== originalTicketStatus)
                  }}
                  options={statusOptions
                    .filter(option => {
                      const optionValue = parseInt(option.value)
                      const currentStatus = parseInt(originalTicketStatus)

                      // If current status is Completed (4), only show Completed
                      if (currentStatus === 4) {
                        return optionValue === 4
                      }

                      // For other statuses, show the current status and higher options
                      return optionValue >= currentStatus
                    })
                    .map(option => ({
                      ...option,
                      isDisabled:
                        parseInt(option.value) ===
                        parseInt(originalTicketStatus),
                    }))}
                  placeholder={t("Update Status")}
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="form_field">
                <label htmlFor="">{t("Remark(Optional)")}</label>
                <textarea
                  placeholder={t("Remark(Optional)")}
                  rows="4"
                  onChange={e => setRemark(e.target.value)}
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-end mt-4">
                <Button type="button" color="secondary" onClick={toggleModal}>
                  {t("Cancel")}
                </Button>
                <Button
                  className="mx-2"
                  color="success"
                  type="button"
                  disabled={!isStatusUpdated || isLoading}
                  onClick={handleStatusChange}
                >
                  {t("Update")}
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default Bookings
